import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useFeeStructureService } from "../apis/feeStructure/useFeeStructureService";
import type {
  TFeeComponentApi,
  TCreateFeeComponentApiRequest,
  TUpdateFeeComponentApiRequest,
  TGetClassFeesResponse,
  TStudentCategory,
  TStudentCategoriesListResponse,
  TCreateStudentCategoryRequest,
  TUpdateStudentCategoryRequest,
  TCategoryComponentAttachment,
  TCategoryComponentAttachmentsListResponse,
  TCreateCategoryComponentAttachmentRequest,
  TUpdateCategoryComponentAttachmentRequest,
} from "../apis/feeStructure/feeStructure.interfaces";
import { useInstituteContext } from "../context/institute.context";

export const useClassFeesHook = (opts?: {
  classId?: string;
  academicYear?: string;
}) => {
  const { getClassFees } = useFeeStructureService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TGetClassFeesResponse>({
    queryKey: [
      "class-fees",
      selectedInstitute?.id,
      opts?.classId,
      opts?.academicYear,
    ],
    queryFn: () => getClassFees(opts),
    enabled: !!selectedInstitute?.id,
  });
};

export const useFeeComponentsHook = (opts?: { activeOnly?: boolean }) => {
  const { listFeeComponents } = useFeeStructureService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TFeeComponentApi[]>({
    queryKey: ["fee-components", selectedInstitute?.id, opts?.activeOnly],
    queryFn: () => listFeeComponents(opts),
    enabled: !!selectedInstitute?.id,
  });
};

export const useStudentCategoriesForClassHook = (classId?: string) => {
  const { listStudentCategoriesForClass } = useFeeStructureService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TStudentCategoriesListResponse, Error, TStudentCategory[]>({
    queryKey: ["student-categories", classId, selectedInstitute?.id],
    queryFn: () => listStudentCategoriesForClass(classId!, { limit: 200 }),
    enabled: !!classId && !!selectedInstitute?.id,
    select: (d) => d.items,
  });
};

export const useCategoryAttachmentsHook = (categoryId?: string) => {
  const { listCategoryComponentAttachments } = useFeeStructureService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<
    TCategoryComponentAttachmentsListResponse,
    Error,
    TCategoryComponentAttachment[]
  >({
    queryKey: ["category-attachments", categoryId, selectedInstitute?.id],
    queryFn: () => listCategoryComponentAttachments(categoryId!),
    enabled: !!categoryId && !!selectedInstitute?.id,
    select: (d) => d.items,
  });
};

export const useCreateFeeComponentHook = () => {
  const { createFeeComponent } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: (request: TCreateFeeComponentApiRequest) =>
      createFeeComponent(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fee-components", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateFeeComponentHook = () => {
  const { updateFeeComponent } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: TUpdateFeeComponentApiRequest;
    }) => updateFeeComponent(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fee-components", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteFeeComponentHook = () => {
  const { deleteFeeComponent } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: (id: string) => deleteFeeComponent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fee-components", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useCreateStudentCategoryHook = () => {
  const { createStudentCategoryForClass } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({
      classId,
      body,
    }: {
      classId: string;
      body: TCreateStudentCategoryRequest;
    }) => createStudentCategoryForClass(classId, body),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["student-categories", v.classId, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateStudentCategoryHook = () => {
  const { updateStudentCategory } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({
      id,
      body,
      classId,
    }: {
      id: string;
      body: TUpdateStudentCategoryRequest;
      classId: string;
    }) => updateStudentCategory(id, body),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["student-categories", v.classId, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteStudentCategoryHook = () => {
  const { deleteStudentCategory } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({ id, classId }: { id: string; classId: string }) =>
      deleteStudentCategory(id),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["student-categories", v.classId, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["category-attachments", v.id, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useCreateCategoryAttachmentHook = () => {
  const { createCategoryComponentAttachment } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({
      categoryId,
      body,
      classId,
    }: {
      categoryId: string;
      body: TCreateCategoryComponentAttachmentRequest;
      classId: string;
    }) => createCategoryComponentAttachment(categoryId, body),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["category-attachments", v.categoryId, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id, v.classId],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateCategoryAttachmentHook = () => {
  const { updateCategoryComponentAttachment } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({
      categoryId,
      ruleId,
      body,
      classId,
    }: {
      categoryId: string;
      ruleId: string;
      body: TUpdateCategoryComponentAttachmentRequest;
      classId: string;
    }) => updateCategoryComponentAttachment(categoryId, ruleId, body),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["category-attachments", v.categoryId, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteCategoryAttachmentHook = () => {
  const { deleteCategoryComponentAttachment } = useFeeStructureService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation({
    mutationFn: ({
      categoryId,
      ruleId,
      classId,
    }: {
      categoryId: string;
      ruleId: string;
      classId: string;
    }) => deleteCategoryComponentAttachment(categoryId, ruleId),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["category-attachments", v.categoryId, selectedInstitute?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["class-fees", selectedInstitute?.id],
      });
    },
  });
};
